import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import { storeService } from '../services/StoreService';
import './ParentStoreView.css';
import './ParentDetailPages.css';

function ParentStoreView({ children, parentData }) {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await storeService.getActiveProducts(parentData?.schoolId);
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  }, [parentData?.schoolId]);

  const loadOrders = useCallback(async () => {
    if (auth.currentUser?.uid) {
      const data = await storeService.getOrdersByParent(auth.currentUser.uid);
      setOrders(data);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadOrders();
  }, [loadProducts, loadOrders]);

  const categories = ['Uniform', 'Books', 'Crafts', 'Accessories', 'Stationery', 'Other'];

  const filteredProducts = selectedCategory
    ? products.filter(p => p.category === selectedCategory)
    : products;

  const formatCurrency = (amount) => `EC$${(amount || 0).toFixed(2)}`;

  const getCategoryIcon = (category) => {
    const icons = { 'Uniform': '👕', 'Books': '📚', 'Crafts': '🎨', 'Accessories': '🎒', 'Stationery': '✏️', 'Other': '🛍️' };
    return icons[category] || '🛍️';
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.productId === product.id);
      if (existing) {
        return prev.map(item =>
          item.productId === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, {
        productId: product.id,
        productName: product.name,
        price: product.price,
        quantity: 1,
      }];
    });
  };

  const updateCartQty = (index, qty) => {
    if (qty <= 0) {
      setCart(prev => prev.filter((_, i) => i !== index));
    } else {
      setCart(prev => prev.map((item, i) => i === index ? { ...item, quantity: qty } : item));
    }
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const placeOrder = async (paymentMethod, selectedChild) => {
    const order = {
      parentId: auth.currentUser.uid,
      parentName: parentData?.username || auth.currentUser?.email?.split('@')[0],
      studentId: selectedChild?.id || null,
      studentName: selectedChild?.fullName || null,
      items: cart,
      totalAmount: cartTotal,
      status: 'Pending',
      paymentMethod,
      schoolId: parentData?.schoolId || null,
    };
    await storeService.placeOrder(order);
    setCart([]);
    setShowCheckout(false);
    setShowCart(false);
    setOrderPlaced(true);
    loadOrders();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading store...</p>
      </div>
    );
  }

  // Full-screen product detail page (replaces the old cramped modal)
  if (selectedProduct) {
    return (
      <ProductDetailPage
        product={selectedProduct}
        formatCurrency={formatCurrency}
        getCategoryIcon={getCategoryIcon}
        onAddToCart={(qty) => {
          for (let i = 0; i < qty; i++) addToCart(selectedProduct);
          setSelectedProduct(null);
        }}
        onBack={() => setSelectedProduct(null)}
      />
    );
  }

  return (
    <div className="parent-store-view">
      <div className="store-header">
        <h2>School Store</h2>
        <div className="store-actions">
          <button className="orders-btn" onClick={() => setShowOrders(true)}>
            📋 My Orders
          </button>
          <button className="cart-btn" onClick={() => setShowCart(true)}>
            🛒 Cart {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="category-filters">
        <button className={!selectedCategory ? 'active' : ''} onClick={() => setSelectedCategory(null)}>All</button>
        {categories.map(cat => (
          <button key={cat} className={selectedCategory === cat ? 'active' : ''} onClick={() => setSelectedCategory(cat)}>
            {getCategoryIcon(cat)} {cat}
          </button>
        ))}
      </div>

      {/* Products */}
      <div className="products-grid">
        {filteredProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🛍️</div>
            <h3>No Products Available</h3>
            <p>Check back later for school items</p>
          </div>
        ) : (
          filteredProducts.map(product => (
            <div key={product.id} className="product-card" onClick={() => setSelectedProduct(product)}>
              <div className="product-card-image-wrapper">
                {product.imageURL ? (
                  <img src={product.imageURL} alt={product.name} className="product-card-image" />
                ) : (
                  <div className="product-icon">{getCategoryIcon(product.category)}</div>
                )}
                <div className="product-card-overlay">
                  <h4>{product.name}</h4>
                  {product.size && <span className="product-size">{product.size}</span>}
                </div>
              </div>
              <div className="product-bottom">
                <div className="product-price-section">
                  <span className="product-price">{formatCurrency(product.price)}</span>
                  <span className={`product-stock ${product.stockQuantity > 0 ? 'in-stock' : 'out-of-stock'}`}>
                    {product.stockQuantity > 0 ? `${product.stockQuantity} in stock` : 'Out of stock'}
                  </span>
                </div>
                <button
                  className="add-cart-btn"
                  onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                  disabled={product.stockQuantity <= 0}
                >
                  +
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Cart Modal */}
      {showCart && (
        <div className="modal-overlay" onClick={() => setShowCart(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Shopping Cart</h3>
              <button className="close-button" onClick={() => setShowCart(false)}>×</button>
            </div>
            <div className="modal-body">
              {cart.length === 0 ? (
                <p className="empty-cart">Your cart is empty</p>
              ) : (
                <>
                  {cart.map((item, index) => (
                    <div key={index} className="cart-item">
                      <div className="cart-item-info">
                        <span className="cart-item-name">{item.productName}</span>
                        <span className="cart-item-price">{formatCurrency(item.price)} each</span>
                      </div>
                      <div className="cart-item-controls">
                        <button onClick={() => updateCartQty(index, item.quantity - 1)}>−</button>
                        <span>{item.quantity}</span>
                        <button onClick={() => updateCartQty(index, item.quantity + 1)}>+</button>
                        <span className="cart-item-subtotal">{formatCurrency(item.price * item.quantity)}</span>
                      </div>
                    </div>
                  ))}
                  <div className="cart-total">
                    <span>Total:</span>
                    <span className="total-amount">{formatCurrency(cartTotal)}</span>
                  </div>
                </>
              )}
            </div>
            {cart.length > 0 && (
              <div className="modal-footer">
                <button className="clear-btn" onClick={() => setCart([])}>Clear</button>
                <button className="checkout-btn" onClick={() => { setShowCart(false); setShowCheckout(true); }}>
                  Checkout
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <CheckoutModal
          cart={cart}
          cartTotal={cartTotal}
          children={children}
          onPlaceOrder={placeOrder}
          onClose={() => setShowCheckout(false)}
        />
      )}

      {/* Order History Modal */}
      {showOrders && (
        <div className="modal-overlay" onClick={() => setShowOrders(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>My Orders</h3>
              <button className="close-button" onClick={() => setShowOrders(false)}>×</button>
            </div>
            <div className="modal-body">
              {orders.length === 0 ? (
                <p className="empty-cart">No orders yet</p>
              ) : (
                orders.map(order => (
                  <div key={order.id} className="order-history-card">
                    <div className="order-history-header">
                      <span className="order-history-total">{formatCurrency(order.totalAmount)}</span>
                      <span className={`order-status ${(order.status || '').toLowerCase()}`}>{order.status}</span>
                    </div>
                    <div className="order-history-items">
                      {order.items?.map((item, i) => (
                        <div key={i} className="order-history-item">
                          {item.productName} x{item.quantity}
                        </div>
                      ))}
                    </div>
                    <span className="order-history-date">
                      {order.createdAt?.toDate ? new Date(order.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order Placed Alert */}
      {orderPlaced && (
        <div className="success-overlay" onClick={() => setOrderPlaced(false)}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✅</div>
            <h3>Order Placed!</h3>
            <p>Your order has been submitted successfully.</p>
            <button onClick={() => setOrderPlaced(false)}>OK</button>
          </div>
        </div>
      )}

    </div>
  );
}

// Full-screen product detail page
function ProductDetailPage({ product, formatCurrency, getCategoryIcon, onAddToCart, onBack }) {
  const [quantity, setQuantity] = useState(1);
  const maxQty = Math.min(product.stockQuantity || 0, 10);

  return (
    <div className="detail-page">
      <button className="detail-back-button" onClick={onBack}>
        ◄ Back to Store
      </button>

      <div className="detail-hero">
        <div className="detail-avatar">
          {product.imageURL ? <img src={product.imageURL} alt={product.name} /> : getCategoryIcon(product.category)}
        </div>
        <div className="detail-hero-info">
          <h2>{product.name}</h2>
          <p className="detail-subtitle">{formatCurrency(product.price)}</p>
          <div className="detail-badges">
            {product.category && <span className="detail-badge">{product.category}</span>}
            {product.size && <span className="detail-badge gray">Size {product.size}</span>}
            <span className={`detail-badge ${product.stockQuantity > 0 ? 'green' : 'red'}`}>
              {product.stockQuantity > 0 ? 'In Stock' : 'Out of Stock'}
            </span>
          </div>
        </div>
      </div>

      <div className="detail-grid">
        {product.imageURL && (
          <div className="detail-card full-width">
            <img src={product.imageURL} alt={product.name} style={{ width: '100%', maxWidth: '480px', borderRadius: '10px', display: 'block', margin: '0 auto' }} />
          </div>
        )}

        <div className="detail-card">
          <h3>Product Details</h3>
          <div className="detail-field"><span className="field-label">Price</span><span className="field-value">{formatCurrency(product.price)}</span></div>
          {product.size && <div className="detail-field"><span className="field-label">Size</span><span className="field-value">{product.size}</span></div>}
          {product.category && <div className="detail-field"><span className="field-label">Category</span><span className="field-value">{product.category}</span></div>}
          <div className="detail-field"><span className="field-label">Stock</span><span className="field-value">{product.stockQuantity > 0 ? `${product.stockQuantity} available` : 'Out of stock'}</span></div>
        </div>

        <div className="detail-card">
          <h3>Description</h3>
          {product.description
            ? <p className="detail-empty" style={{ color: '#374151' }}>{product.description}</p>
            : <p className="detail-empty">No description provided</p>}
        </div>

        {product.stockQuantity > 0 && (
          <div className="detail-card full-width">
            <h3>Order</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button className="detail-btn detail-btn-secondary" onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>−</button>
              <span style={{ minWidth: '40px', textAlign: 'center', fontSize: '18px', fontWeight: 700 }}>{quantity}</span>
              <button className="detail-btn detail-btn-secondary" onClick={() => setQuantity(Math.min(maxQty, quantity + 1))} disabled={quantity >= maxQty}>+</button>
              <span style={{ marginLeft: 'auto', color: '#14532d', fontWeight: 700, fontSize: '18px' }}>
                {formatCurrency(product.price * quantity)}
              </span>
            </div>
            <button
              className="detail-btn detail-btn-edit"
              style={{ marginTop: '16px', width: '100%' }}
              onClick={() => onAddToCart(quantity)}
            >
              🛒 Add to Cart
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CheckoutModal({ cart, cartTotal, children, onPlaceOrder, onClose }) {
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [selectedChildIndex, setSelectedChildIndex] = useState(0);
  const [processing, setProcessing] = useState(false);

  const formatCurrency = (amount) => `EC$${(amount || 0).toFixed(2)}`;

  const handlePlaceOrder = async () => {
    setProcessing(true);
    const selectedChild = children?.[selectedChildIndex] || null;
    await onPlaceOrder(paymentMethod, selectedChild);
    setProcessing(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Checkout</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <h4>Order Summary</h4>
          {cart.map((item, i) => (
            <div key={i} className="checkout-item">
              <span>{item.productName} x{item.quantity}</span>
              <span>{formatCurrency(item.price * item.quantity)}</span>
            </div>
          ))}
          <div className="checkout-total">
            <span>Total:</span>
            <span>{formatCurrency(cartTotal)}</span>
          </div>

          {children && children.length > 0 && (
            <>
              <h4>For Child</h4>
              <select value={selectedChildIndex} onChange={(e) => setSelectedChildIndex(parseInt(e.target.value))}>
                {children.map((child, i) => (
                  <option key={i} value={i}>{child.fullName || `Child ${i + 1}`}</option>
                ))}
              </select>
            </>
          )}

          <h4>Payment Method</h4>
          <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            <option value="Cash">Cash</option>
            <option value="Stripe">Card (Stripe)</option>
          </select>
        </div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="checkout-btn" onClick={handlePlaceOrder} disabled={processing}>
            {processing ? 'Placing Order...' : 'Place Order'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ParentStoreView;
